/**
 * CircleContext.tsx - Circle Management Context
 *
 * Provides circle-related state and functionality throughout the application.
 * Manages parent circles, child circles (user-specific), and circle permissions.
 * Handles automatic child circle creation and parent circle joining.
 */

import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useCircleContext as useCoreCircleContext, useSubCircles, useCirclePostsState } from 'core';
import { useParams } from 'react-router';
import { getAvatarUrl } from '@/utilities/imageUtils';

interface CircleContextState {
  parentCircleId: string;
  childCircleId: string | null;
  setChildCircleId: (id: string | null) => void;
  circleName: string;
  circleImageUrl: string;
  parentCircleName: string;
  parentCircleImageUrl: string;
  subCircles: any[];
  permissionGranted: boolean;
  setPermissionGranted: (granted: boolean) => void;
  permissionDenied: boolean;
  coreCircleData: any;
  parentCircleData: any;
  childCircleData: any;
  postsState: any;
  posts: any[];
  postsLoaded: boolean;
  firstLoaded: boolean;
  moreLoading: boolean;
  loadMore: () => void;
  updatePost: (post: any) => void;
  createChildCircle: () => Promise<void>;
  requestGroupAccess: () => Promise<boolean>;
  deletePost: (_id: string) => Promise<void>;
}

const CircleContext = createContext<CircleContextState | undefined>(undefined);

interface CircleProviderProps {
  children: ReactNode;
  profile?: any;
  api: any;
  recentKey?: number;
  setRecentKey?: (key: number) => void;
  postId?: string;
  setPostId?: (id: string) => void;
}

export const CircleProvider: React.FC<CircleProviderProps> = ({
  children,
  profile,
  api,
  recentKey,
  setRecentKey,
  postId,
  setPostId
}) => {
  const { circleId: routeCircleId } = useParams();

  const [childCircleId, setChildCircleId] = useState<string | null>(null);
  const [permissionGranted, setPermissionGranted] = useState<boolean>(false);
  const [fallbackJoinAttempted, setFallbackJoinAttempted] = useState<boolean>(false);
  const creatingChildCircleRef = useRef(false);

  const parentCircleId = routeCircleId || "";

  const coreCircleData = useCoreCircleContext();
  const { data: subCircles, error: subCirclesError } = useSubCircles(parentCircleId);

  // Only fetch posts if childCircleId is available (prevents /groups/null requests)
  const postsState = useCirclePostsState(childCircleId, { limit: 30 });
  const {
    firstLoaded,
    postsLoaded,
    all: posts,
    updatePost,
    moreLoading,
    loadMore,
    loadError,
  } = postsState;

  // Detect permission denied (403) from subCircles loading OR posts loading
  const [permissionDenied, setPermissionDenied] = useState<boolean>(false);

  useEffect(() => {
    const subCirclesStatus = (subCirclesError as any)?.status;
    const loadErrorStatus = (loadError as any)?.status;
    if (subCirclesStatus === 403 || loadErrorStatus === 403) {
      setPermissionDenied(true);
      setPermissionGranted(false);
    }
  }, [subCirclesError, loadError]);

  const currentChildCircle = childCircleId
    ? subCircles?.find(circle => circle._id === childCircleId)
    : null;

  const circleName = currentChildCircle?.name || coreCircleData?.circle?.name || '';
  const circleImageUrl = getAvatarUrl(currentChildCircle?.image || coreCircleData?.circle?.image);
  const parentCircleName = coreCircleData?.circle?.name || '';
  const parentCircleImageUrl = getAvatarUrl(coreCircleData?.circle?.image);

  const deletePost = async (_id: string) => {
    try {
      await api.unpublishPost({ _id });

      if (setRecentKey && recentKey !== undefined) {
        setRecentKey(recentKey + 1);
      }

      if (_id === postId && setPostId) {
        setPostId("");
      }
    } catch (error) {
      console.error('Error deleting post:', error);
      throw error;
    }
  };

  useEffect(() => {
    if (permissionDenied || fallbackJoinAttempted) return;
    if (profile && parentCircleId && coreCircleData) {
      const isMember = coreCircleData.membership || subCircles?.length > 0;

      if (!isMember) {
        requestGroupAccess().catch(() => {});
        setFallbackJoinAttempted(true);
      }
    }
  }, [profile, parentCircleId, coreCircleData, subCircles, fallbackJoinAttempted, permissionDenied]);

  useEffect(() => {
    if (!subCircles || !profile) {
      return;
    }

    for (const childCircle of subCircles) {
      if (childCircle.name === profile.username + " [" + profile._id + "]") {
        setChildCircleId(childCircle._id);
        return;
      }
    }

    if (!creatingChildCircleRef.current) {
      creatingChildCircleRef.current = true;
      createChildCircle().finally(() => {
        creatingChildCircleRef.current = false;
      });
    }
  }, [profile, subCircles]);

  const createChildCircle = async () => {
    if (!profile || !parentCircleId) return;

    try {
      const circle = await api.upsertGroup({
        "parent": parentCircleId,
        "privacy": "PERSONAL",
        "min_privacy": "PERSONAL",
        "name": profile.username + " [" + profile._id + "]",
        "image": profile.avatar,
      });

      if (circle?._id) {
        setChildCircleId(circle._id);
      }
    } catch (error) {
      console.error('Error creating child circle:', error);
    }
  };

  const requestGroupAccess = async (): Promise<boolean> => {
    if (!parentCircleId) return false;

    try {
      await api.requestGroupAccess({
        "group": parentCircleId
      });
      return true;
    } catch (error: any) {
      console.error('Error requesting group access:', error);
      throw error;
    }
  };

  const value: CircleContextState = {
    parentCircleId,
    childCircleId,
    setChildCircleId,
    circleName,
    circleImageUrl,
    parentCircleName,
    parentCircleImageUrl,
    subCircles: subCircles || [],
    permissionGranted,
    setPermissionGranted,
    permissionDenied,
    postsState,
    posts,
    postsLoaded,
    firstLoaded,
    moreLoading,
    loadMore,
    updatePost,
    coreCircleData,
    parentCircleData: coreCircleData,
    childCircleData: currentChildCircle,
    createChildCircle,
    requestGroupAccess,
    deletePost,
  };

  return (
    <CircleContext.Provider value={value}>
      {children}
    </CircleContext.Provider>
  );
};

export const useCircleContext = (): CircleContextState => {
  const context = useContext(CircleContext);
  if (context === undefined) {
    throw new Error('useCircleContext must be used within a CircleProvider');
  }
  return context;
};
